/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as THREE from 'three';
import * as ThreeMeshUI from 'three-mesh-ui';

import { RemotePlayerComponent } from '../../components/MultiplayerComponents';
import { System } from 'ecsy';
import { THREEGlobalComponent } from '../../components/THREEGlobalComponent';
import { getOnlyEntity } from '../../utils/entityUtils';

export class RemotePlayerSystem extends System {
	init() {
		this.threeGlobal = null;
	}

	execute(_delta) {
		if (!this.threeGlobal) {
			const entity = getOnlyEntity(this.queries.threeGlobal, false);
			if (entity) {
				this.threeGlobal = entity.getComponent(THREEGlobalComponent);
			}
		}

		if (!this.threeGlobal) return;
		this.queries.remotePlayers.added.forEach((entity) => {
			this._initRemotePlayerMesh(entity);
		});

		this.queries.remotePlayers.removed.forEach((entity) => {
			this._removeRemotePlayerMesh(entity);
		});

		this.queries.remotePlayers.results.forEach((entity) => {
			this._updateRemotePlayerMesh(entity, _delta);
		});
	}

	_initRemotePlayerMesh(entity) {
		const remotePlayer = entity.getMutableComponent(RemotePlayerComponent);

		// Body Mesh (Capsule-like)
		const bodyGroup = new THREE.Group();
		const bodyMesh = new THREE.Mesh(
			new THREE.CapsuleGeometry(0.25, 1.0, 4, 8),
			new THREE.MeshStandardMaterial({ color: Math.random() * 0xffffff }),
		);
		bodyMesh.position.y = 0.75;
		bodyGroup.add(bodyMesh);

		// Head Mesh
		const headGroup = new THREE.Group();
		const headMesh = new THREE.Mesh(
			new THREE.SphereGeometry(0.15, 16, 16),
			new THREE.MeshStandardMaterial({ color: 0xffdbac }),
		);
		headGroup.add(headMesh);
		bodyGroup.add(headGroup);

		// Name Tag (ThreeMeshUI)
		const nameTag = new ThreeMeshUI.Block({
			width: 0.8,
			height: 0.2,
			padding: 0.05,
			justifyContent: 'center',
			alignItems: 'center',
			backgroundColor: new THREE.Color(0x000000),
			backgroundOpacity: 0.5,
			borderRadius: 0.05,
		});

		const nameText = new ThreeMeshUI.Text({
			content: `${remotePlayer.name}\n(${remotePlayer.country})`,
			fontSize: 0.05,
			fontFamily: 'assets/fonts/Roboto-Regular-48.json',
			fontTexture: 'assets/fonts/Roboto-Regular-48.png',
		});

		nameTag.add(nameText);
		nameTag.position.y = 1.6; // Position above head
		bodyGroup.add(nameTag);

		this.threeGlobal.scene.add(bodyGroup);

		remotePlayer.model = bodyGroup;
		remotePlayer.head = headGroup;
		remotePlayer.nameTag = nameTag;
	}

	_removeRemotePlayerMesh(entity) {
		const remotePlayer = entity.getComponent(RemotePlayerComponent);
		if (remotePlayer.model) {
			this.threeGlobal.scene.remove(remotePlayer.model);
		}
	}

	_updateRemotePlayerMesh(entity, _delta) {
		const remotePlayer = entity.getComponent(RemotePlayerComponent);
		if (!remotePlayer.model) return;

		// Smoothly interpolate towards target transform (Lerp/Slerp)
		remotePlayer.model.position.lerp(remotePlayer.targetPos, 0.1);
		remotePlayer.model.quaternion.slerp(remotePlayer.targetRot, 0.1);

		remotePlayer.head.position.lerp(remotePlayer.targetHeadPos, 0.1);
		remotePlayer.head.quaternion.slerp(remotePlayer.targetHeadRot, 0.1);

		// Make name tag look at local camera
		remotePlayer.nameTag.lookAt(this.threeGlobal.camera.position);
	}
}

RemotePlayerSystem.queries = {
	threeGlobal: { components: [THREEGlobalComponent] },
	remotePlayers: {
		components: [RemotePlayerComponent],
		listen: { added: true, removed: true },
	},
};
